/**
 * Stored voiceprints between the API and the screens: a person's voiceprints and open voice
 * leads as the subject page reads them, and the small shapes the other voice mappers share.
 * A value the workspace does not know (an origin, a state, a quality flag from a newer server)
 * is left out rather than guessed; a voiceprint without a known origin or state is not shown.
 */
import { voiceprintShortId } from '@/components/humint/voice/voice-glyph'
import {
  type SubjectVoiceLeads,
  VOICE_QUALITY_FLAGS,
  VOICEPRINT_ORIGINS,
  VOICEPRINT_STATES,
  type VoiceBand,
  type VoiceCondition,
  type VoiceprintBrief,
  type VoiceprintOrigin,
  type VoiceprintState,
  type VoiceprintView,
  type VoiceQualityFlag,
  type VoiceQualityView,
} from '@/components/humint/voice/voiceprint-types'
import type {
  SubjectVoiceLeadsDto,
  VoiceConditionDto,
  VoiceprintBriefDto,
  VoiceprintDto,
} from '@/services/api/humint-voice-matching'
import { known, present } from '../mappers/known'

const origin = known<VoiceprintOrigin>(VOICEPRINT_ORIGINS)
const state = known<VoiceprintState>(VOICEPRINT_STATES)
const qualityFlag = known<VoiceQualityFlag>(VOICE_QUALITY_FLAGS)
const qualityLevel = known<VoiceQualityView['level']>(['ok', 'low'])
const band = known<VoiceBand>(['weak', 'possible', 'strong'])
const conditionId = known<VoiceCondition['id']>(['similarity', 'quality', 'agreement', 'margin'])

/** A voiceprint as a list of people names it; null for an origin or a state not known here. */
export function toVoiceprintBrief(dto: VoiceprintBriefDto): VoiceprintBrief | null {
  const knownOrigin = origin(dto.origin)
  const knownState = state(dto.state)
  if (!knownOrigin || !knownState) return null
  return {
    id: dto.voiceprint_id,
    shortId: voiceprintShortId(dto.voiceprint_id),
    origin: knownOrigin,
    state: knownState,
  }
}

/** The sound as the server judged it; null where it did not, so nothing is drawn. */
export function toVoiceQuality(
  dto: Pick<VoiceprintDto, 'quality' | 'quality_flags'>
): VoiceQualityView | null {
  const level = qualityLevel(dto.quality)
  if (!level) return null
  return { level, flags: dto.quality_flags.map(qualityFlag).filter(present) }
}

/** One stored voiceprint; null for an origin or a state not known here. */
export function toVoiceprint(dto: VoiceprintDto): VoiceprintView | null {
  const brief = toVoiceprintBrief(dto)
  if (!brief) return null
  return {
    ...brief,
    captureId: dto.capture_id,
    // A sample is no voice of a recording, whatever the server sent with it.
    voice: brief.origin === 'questioning' ? dto.voice : null,
    encounterRef: dto.encounter_ref,
    createdAt: dto.created_at,
    speechMs: dto.speech_ms,
    quality: toVoiceQuality(dto),
    used: dto.reference && brief.state === 'ready',
    pendingCandidates: dto.pending_candidates,
    audioUrl: dto.audio_url,
  }
}

/** A person's voiceprints in the order the server sent them; none from a server that sends none. */
export function toVoiceprints(dtos: readonly VoiceprintDto[] | null | undefined): VoiceprintView[] {
  return (dtos ?? []).map(toVoiceprint).filter(present)
}

/** A person's open voice leads; none from a server that counts none. */
export function toSubjectVoiceLeads(
  dto: SubjectVoiceLeadsDto | null | undefined
): SubjectVoiceLeads {
  const count = Math.max(0, dto?.count ?? 0)
  const open = count > 0
  return {
    count,
    pairId: open ? (dto?.pair_id ?? null) : null,
    voiceprintId: open ? (dto?.voiceprint_id ?? null) : null,
  }
}

/**
 * The band a comparison shows, as the server sent it: `weak` or `possible` (a provisional strong
 * arrives as possible). Anything else reads possible.
 */
export function toVoiceBand(value: string): VoiceBand {
  return band(value) ?? 'possible'
}

/** What the server looked at, in its order; a condition not known here is left out. */
export function toVoiceConditions(dtos: readonly VoiceConditionDto[]): VoiceCondition[] {
  return dtos
    .map(dto => {
      const id = conditionId(dto.id)
      return id ? { id, met: dto.met, value: dto.value } : null
    })
    .filter(present)
}
